import { useEffect, useRef, useState } from 'react'
import { apiGet, apiPost, ApiError } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import { ImportContacts } from './ImportContacts.tsx'

/**
 * Keying a returned walk sheet (docs/campaign-workflow.md).
 *
 * The whole screen is one focused input. A barcode scanner is a keyboard: it
 * types the code and presses enter, so a volunteer holding a stack of paper
 * never touches the mouse and never looks up. Scan the voter's row, scan a
 * response off the code sheet, repeat. That is the interaction, and everything
 * here exists to keep it uninterrupted.
 *
 * Both codes carry the export number, so the screen works out which walk is on
 * the desk from the first scan rather than asking. A code from a different walk
 * is refused rather than keyed against the wrong meaning.
 */

interface Context {
  export_id: string
  campaign_id: string
  campaign_name: string
  export_name: string | null
  list_type_code: string
  list_type_name: string
  response_codes: { token: string; label: string; disposition: string }[]
  row_count: string | null
  status: string
  keyed: string
}

interface Row {
  export_id: string
  campaign_id: string
  person_id: string
  name: string | null
  address: string | null
  in_universe: boolean | null
  already_keyed: string
}

interface LogLine {
  id: string
  text: string
  detail: string
  tone: 'ok' | 'warn'
}

/** `13-41` is a voter row; `13-0-4` is a response. */
function parseCode(raw: string): { exportNo: string; rowNo: number } | { exportNo: string; ordinal: number } | null {
  const parts = raw.trim().split('-')
  if (parts.length === 2 && /^\d+$/.test(parts[0]!) && /^\d+$/.test(parts[1]!)) {
    return { exportNo: parts[0]!, rowNo: Number(parts[1]) }
  }
  if (parts.length === 3 && parts[1] === '0' && /^\d+$/.test(parts[0]!) && /^\d+$/.test(parts[2]!)) {
    return { exportNo: parts[0]!, ordinal: Number(parts[2]) }
  }
  return null
}

/** A list type's code decides how the voter was contacted, not how it was keyed. */
function methodFor(listTypeCode: string): string {
  if (listTypeCode === 'phone') return 'phone'
  if (listTypeCode === 'walk') return 'walk'
  return 'other'
}

export function Keying() {
  // The scanner works out its campaign from the first barcode; the importer
  // cannot, because a file carries no export number of its own.
  const { campaignId } = useSelectedCampaign()
  const [context, setContext] = useState<Context | null>(null)
  const [row, setRow] = useState<Row | null>(null)
  const [canvasser, setCanvasser] = useState('')
  const [occurredOn, setOccurredOn] = useState('')
  const [log, setLog] = useState<LogLine[]>([])
  const [error, setError] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // The input must never lose focus: a scanner types wherever the caret is, and
  // a stray click on the page would send the next barcode into nothing.
  useEffect(() => {
    const focus = () => inputRef.current?.focus()
    focus()
    window.addEventListener('click', focus)
    return () => window.removeEventListener('click', focus)
  }, [])

  function note(text: string, detail: string, tone: LogLine['tone'] = 'ok') {
    setLog((previous) => [{ id: crypto.randomUUID(), text, detail, tone }, ...previous].slice(0, 60))
  }

  async function loadContext(exportNo: string): Promise<Context | null> {
    if (context) return context
    try {
      const loaded = await apiGet<Context>(`/keying/${exportNo}`)
      setContext(loaded)
      return loaded
    } catch {
      setError(`No list numbered ${exportNo} on a campaign you can reach.`)
      return null
    }
  }

  async function handleVoter(exportNo: string, rowNo: number) {
    // The export number was already checked against the session in submit();
    // this only has to load the context the first time.
    if (!(await loadContext(exportNo))) return

    try {
      const resolved = await apiGet<Row>(`/keying/${exportNo}/row/${rowNo}`)
      setRow(resolved)
      setError(null)
      if (Number(resolved.already_keyed) > 0) {
        note(resolved.name ?? 'Row ' + rowNo, `already keyed ${resolved.already_keyed}×`, 'warn')
      }
    } catch {
      setError(`Row ${rowNo} is not on this list.`)
      setRow(null)
    }
  }

  async function handleResponse(exportNo: string, ordinal: number) {
    if (!context) {
      setError('Scan a voter row first — the response codes belong to a list.')
      return
    }
    if (!row) {
      setError('Scan the voter’s barcode first, then the response.')
      return
    }
    const code = context.response_codes[ordinal - 1]
    if (!code) {
      setError(`Code ${ordinal} is not on this list’s code sheet.`)
      return
    }

    try {
      await apiPost('/contacts', {
        campaign_id: context.campaign_id,
        contacts: [
          {
            // Minted here, before the request leaves: it is the idempotency key,
            // so a retry after a dropped connection is the same knock.
            event_id: crypto.randomUUID(),
            person_id: row.person_id,
            method: methodFor(context.list_type_code),
            source: 'paper',
            disposition: code.disposition,
            response_code: code.token,
            export_id: context.export_id,
            ...(canvasser.trim() ? { canvasser: canvasser.trim() } : {}),
            // The walk happened on Saturday; this is Monday. Reporting on the
            // keying date would credit the wrong day.
            ...(occurredOn ? { occurred_at: new Date(occurredOn).toISOString() } : {}),
          },
        ],
      })
      note(row.name ?? row.person_id, code.label, 'ok')
      setRow(null)
      setError(null)
      setContext({ ...context, keyed: String(Number(context.keyed) + 1) })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not save. Scan it again.')
    }
  }

  /**
   * The export every code in this session must belong to, taken from the first
   * scan. A ref rather than state: it is read inside the submit handler and
   * must be the current value, not the one captured when the handler was made.
   */
  const loadedExportNo = useRef<string | null>(null)

  async function submit(raw: string) {
    setInput('')
    const parsed = parseCode(raw)
    if (!parsed) {
      setError(`“${raw}” is not a Blocwork code.`)
      return
    }
    if (loadedExportNo.current === null) loadedExportNo.current = parsed.exportNo
    if (parsed.exportNo !== loadedExportNo.current) {
      // The mistake this catches: two walks' paperwork on one desk.
      setError(
        `That code belongs to list ${parsed.exportNo}, and you are keying list ${loadedExportNo.current}.`,
      )
      return
    }
    if ('rowNo' in parsed) await handleVoter(parsed.exportNo, parsed.rowNo)
    else await handleResponse(parsed.exportNo, parsed.ordinal)
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold tracking-tight">Key a sheet</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Scan the voter’s barcode, then scan a response from the code sheet. Repeat down the page.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (input.trim()) void submit(input)
        }}
        className="mt-5"
      >
        <label htmlFor="scan" className="mb-1 block text-sm font-medium">
          Scan
        </label>
        <input
          id="scan"
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder="13-41"
          className="w-full rounded-md border-2 border-brand-500 bg-surface px-3 py-3 font-mono text-lg text-ink outline-none"
        />
      </form>

      {error ? (
        <p role="alert" className="mt-3 rounded-md border border-gold-500 bg-surface-sunken px-3 py-2 text-sm text-ink">
          {error}
        </p>
      ) : null}

      {context ? (
        <p className="mt-3 text-xs text-ink-muted">
          {context.export_name ?? context.list_type_name} · {context.campaign_name} ·{' '}
          {context.keyed} of {context.row_count} rows keyed
        </p>
      ) : (
        <p className="mt-3 text-xs text-ink-muted">Scan any row to begin.</p>
      )}

      {/* The confirmation. Big, because the whole reason it exists is to catch a
          scan of the wrong line before it becomes a contact against the wrong
          person — and it has to be readable without leaning in. */}
      {row ? (
        <div className="mt-4 rounded-lg border-2 border-brand-500 bg-surface-sunken px-4 py-3">
          <div className="text-lg font-semibold">{row.name ?? '(no name printed)'}</div>
          <div className="text-sm text-ink-muted">{row.address ?? '(no address printed)'}</div>
          <div className="mt-1 text-xs text-ink-muted">
            {row.in_universe === false ? 'household context — not in the universe · ' : ''}
            now scan a response
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-4">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
            Canvasser <span className="normal-case">(optional)</span>
          </span>
          <input
            value={canvasser}
            onChange={(e) => setCanvasser(e.target.value)}
            placeholder="who walked this sheet"
            className="w-56 rounded-md border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
            Walked on <span className="normal-case">(optional)</span>
          </span>
          <input
            type="date"
            value={occurredOn}
            onChange={(e) => setOccurredOn(e.target.value)}
            className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">
        This session
      </h2>
      <ul className="mt-2 divide-y divide-line border-y border-line text-sm">
        {log.length === 0 ? (
          <li className="py-3 text-ink-muted">Nothing keyed yet.</li>
        ) : (
          log.map((line) => (
            <li key={line.id} className="flex items-baseline justify-between gap-4 py-2">
              <span>{line.text}</span>
              <span className={line.tone === 'warn' ? 'text-xs font-semibold' : 'text-xs text-ink-muted'}>
                {line.detail}
              </span>
            </li>
          ))
        )}
      </ul>

      {/* Append-only (§9). A mis-keyed response is corrected by scanning the
          row again and giving the right answer; the later event wins and both
          survive, which is the honest record of what happened. */}
      <p className="mt-3 text-xs text-ink-muted">
        Keyed a wrong response? Scan that row again and scan the right one. Nothing is overwritten —
        the later answer is the one that counts.
      </p>

      {campaignId ? <ImportContacts campaignId={campaignId} /> : null}
    </div>
  )
}
